import React from 'react';
import { ExclamationTriangleIcon, XCircleIcon, BanknotesIcon, XMarkIcon, PlayIcon } from '@heroicons/react/24/outline';

interface TokenAlertDialogProps {
  isOpen: boolean;
  type: 'warning' | 'zero' | 'overdraft';
  title: string;
  message: string;
  onRecharge: () => void;
  onClose: () => void;
  onContinue?: () => void; // 继续生成回调（欠费但未超过最大欠费额度时可用）
  canContinue?: boolean; // 是否显示"继续生成"按钮
}

const TokenAlertDialog: React.FC<TokenAlertDialogProps> = ({
  isOpen,
  type,
  title,
  message,
  onRecharge,
  onClose,
  onContinue,
  canContinue = false
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'overdraft':
        return <XCircleIcon className="w-12 h-12 text-red-500" />;
      case 'zero':
        return <ExclamationTriangleIcon className="w-12 h-12 text-orange-500" />;
      case 'warning':
      default:
        return <ExclamationTriangleIcon className="w-12 h-12 text-yellow-500" />;
    }
  };

  const getButtonStyle = () => {
    switch (type) {
      case 'overdraft':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'zero':
        return 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500';
      case 'warning':
      default:
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
    }
  };

  // 所有类型的弹窗都可以关闭（欠费状态允许关闭查看当前内容）
  const canClose = true;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* 遮罩层 - 点击遮罩关闭 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={canClose ? onClose : undefined} />

      {/* 弹窗内容 */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-fade-in">
        {/* 右上角关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
        </button>

        {/* 图标 */}
        <div className="flex justify-center mb-4">
          <div className="p-3 rounded-full bg-gray-50">
            {getIcon()}
          </div>
        </div>

        {/* 标题 */}
        <h3 className="text-xl font-bold text-center text-gray-900 mb-3">
          {title}
        </h3>

        {/* 消息 */}
        <p className="text-gray-600 text-center mb-6 leading-relaxed">
          {message}
        </p>

        {/* 按钮组 */}
        <div className="flex gap-3">
          {/* AI调用返回402时，显示"继续生成"按钮 */}
          {canContinue && onContinue && (
            <button
              onClick={onContinue}
              className="flex-1 px-4 py-2.5 rounded-xl border border-blue-300 text-blue-600 font-medium hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <PlayIcon className="w-5 h-5" />
              继续生成
            </button>
          )}
          {/* 预警状态显示"稍后再说" */}
          {type === 'warning' && (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              稍后再说
            </button>
          )}
          <button
            onClick={onRecharge}
            className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium flex items-center justify-center gap-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${getButtonStyle()}`}
          >
            <BanknotesIcon className="w-5 h-5" />
            立即充值
          </button>
        </div>

        {/* 欠费状态提示 */}
        {type === 'overdraft' && (
          <p className="mt-4 text-xs text-center text-red-500">
            已达最大欠费额度，AI 功能已暂停使用
          </p>
        )}
        {/* 欠费但未超额度提示 */}
        {type === 'zero' && (
          <p className="mt-4 text-xs text-center text-orange-500">
            欠费未超过额度上限，可继续使用 AI 功能
          </p>
        )}
      </div>
    </div>
  );
};

export default TokenAlertDialog;
